import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import './App.css';

// Layout components
import { Header, Sidebar, HomePage, Footer, AuthGate } from './components/layout';
import { AlertBar, DynamicModulePage, UnderConstruction, PermissionGuard, GlobalPermissionGuard, SysAdminGuard } from './components/common';
import { NotificationProvider } from './context/NotificationContext';
import { EntraAuthProvider, ImpersonationAuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import MsalProviderWrapper from './auth/MsalProviderWrapper';
import { isEntraAuth } from './auth/authConfig';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DatabasePickerPage from './pages/DatabasePickerPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';

// Development pages
import { SampleTableViewPage, TaskLogPage, DevPlanPage, SchemaViewerPage, FieldManagerPage, ClaudeNotesPage, RevLogPage, ListViewConfigPage, DevelopmentDashboardPage, IconBrowserPage, ImportTemplatePage, LookupValuesPage, DisplaySettingsPage, PowerQueryPage, SyncDashboardPage, ViewSyncPage, StyleGuidePage, DataEntryComposerPage, SchemaSyncPage } from './modules/development';

// Utilities pages
import { CombinedToolsPage, UtilitiesDashboardPage } from './modules/utilities';

// Integration pages
import { IntegrationDashboardPage, PlcPlatformsPage, PlcTagXRefPage, UdtCommentPage, ScadaTagGenerationPage, SimulationTagGenerationPage } from './modules/integration';

// Data Entry module pages
import { DataEntryModulePage, DataEntryDashboardPage } from './modules/data-entry';

// Supporting Documentation pages
import { SupportingDocsDashboardPage, SupportingDocsGroupPage } from './modules/supporting-docs';
import { LookupProvider } from './context/LookupContext';
import { ActivityTrackerProvider } from './context/ActivityTrackerContext';

// Activity + Preferences pages
import ActivityHistoryPage from './modules/activity/ActivityHistoryPage';
import PreferencesPage from './modules/preferences/PreferencesPage';

// Dashboard pages
import { DocumentationDashboardPage, NetworkingDashboardPage, EquipmentDashboardPage } from './modules/dashboards';

// Report pages
import { ReportViewerPage, ReportsPage } from './modules/reports';

// Administration pages
import { SecurityOverviewPage, SecurityDashboardPage, AdminUsersPage, RolesCapabilitiesPage, PermissionsPage, DatabasesPage, OrganizationsPage, SystemHealthPage } from './modules/administration';

// Config
import { getPageDisplayName } from './config/navigationConfig';
import { adminManagementApi } from './services/api';

// Pages that do NOT need a database slug (admin DB / dev tools)
const SLUG_FREE_PREFIXES = ['admin/', 'development-dashboard', 'sample-table-view', 'task-log', 'dev-plan', 'schema-viewer', 'field-manager', 'claude-notes', 'icon-browser', 'rev-log', 'standard-view-config', 'lookup-values', 'display-settings', 'database-sync', 'view-synchronizer', 'schema-synchronizer', 'style-guide', 'data-entry-view-config'];

const isSlugFreePage = (page) => SLUG_FREE_PREFIXES.some(p => page.startsWith(p));


/**
 * DbRedirect — redirects bare "/" to the database picker.
 * Slug must be explicit in the URL — no stored-slug fallback.
 */
const DbRedirect = () => {
  return <Navigate to="/select-database" replace />;
};


/**
 * AdminLayout — shared layout for slug-free pages (admin + dev tools).
 * Uses Outlet to render the matched child route.
 */
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, accessDenied, loading: authLoading } = useAuth();

  const [currentPage, setCurrentPageState] = useState('');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pagePath = location.pathname.slice(1) || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  // Set browser title from active organization
  useEffect(() => {
    adminManagementApi.getOrganizations()
      .then(res => {
        const active = res.data.find(o => o.isActive) || res.data[0];
        if (active?.displayName) {
          document.title = `DAWN - ${active.displayName}`;
        }
      })
      .catch(() => {}); // silently fail — title stays as "DAWN - "
  }, []);

  // Navigate using router
  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      const lastSlug = localStorage.getItem('pmd-selected-db-slug');
      navigate(lastSlug ? `/${lastSlug}/${page === 'home' ? '' : page}` : '/select-database');
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  if (!authLoading && accessDenied) return <AccessDeniedPage />;

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => {
          const lastSlug = localStorage.getItem('pmd-selected-db-slug');
          navigate(lastSlug ? `/${lastSlug}/` : '/select-database');
        }}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/** Slug-aware redirect — preserves the current dbSlug in the target path. */
const SlugRedirect = ({ to }) => {
  const { dbSlug } = useDatabase();
  return <Navigate to={`/${dbSlug}/${to}`} replace />;
};

/**
 * AppContent — layout for data pages under /:dbSlug/*.
 */
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSysAdmin, accessDenied, currentUser, loading } = useAuth();
  const { dbSlug } = useDatabase();

  const [currentPage, setCurrentPageState] = useState('home');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const pagePath = pathParts.slice(1).join('/') || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      navigate(`/${dbSlug}/${page === 'home' ? '' : page}`);
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  const zeroRoles = !loading && !isSysAdmin && currentUser && Object.keys(currentUser?.roles || {}).length === 0;
  if (accessDenied || zeroRoles) {
    return <AccessDeniedPage />;
  }

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => setCurrentPage('home')}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />
            <Route path="/home" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />

            {/* Supporting Documentation dashboards */}
            <Route path="/supportingdocs-dashboard"    element={<SupportingDocsDashboardPage />} />
            <Route path="/supportingdocs-general"      element={<SupportingDocsGroupPage groupId="general" />} />
            <Route path="/supportingdocs-panels"       element={<SupportingDocsGroupPage groupId="panels" />} />
            <Route path="/supportingdocs-tags"         element={<SupportingDocsGroupPage groupId="tags" />} />
            <Route path="/supportingdocs-index"        element={<SupportingDocsGroupPage groupId="index" />} />
            <Route path="/supportingdocs-network"      element={<SupportingDocsGroupPage groupId="network" />} />
            <Route path="/supportingdocs-commissioning" element={<SupportingDocsGroupPage groupId="commissioning" />} />

            {/* Dynamic module routes - project DB data */}
            <Route path="/buildings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/source/:recordId?" element={<DynamicModulePage />} />
            <Route path="/discipline/:recordId?" element={<DynamicModulePage />} />
            <Route path="/commissioning-key/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawing-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-sln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-dln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/organization/:recordId?" element={<DynamicModulePage />} />
            <Route path="/card-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-enet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-serial/:recordId?" element={<DynamicModulePage />} />
            <Route path="/plc-msg-map/:recordId?" element={<DynamicModulePage />} />
            <Route path="/tags/:recordId?" element={<DynamicModulePage />} />
            <Route path="/eng-units/:recordId?" element={<DynamicModulePage />} />
            <Route path="/isa-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/index-sheet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/manufacturer/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-terminal/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panels/:recordId?" element={<DynamicModulePage />} />
            <Route path="/power-distribution-dashboard" element={<SlugRedirect to="pwr-dist" />} />
            <Route path="/pwr-dist/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-index/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-equipment/:recordId?" element={<DynamicModulePage />} />
            <Route path="/subnet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/rfi-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/system-code/:recordId?" element={<DynamicModulePage />} />
            <Route path="/transfer-party/:recordId?" element={<DynamicModulePage />} />
            <Route path="/valve-size/:recordId?" element={<DynamicModulePage />} />
            <Route path="/wire-type/:recordId?" element={<DynamicModulePage />} />

            {/* Data Entry module pages */}
            <Route path="/data-entry-dashboard" element={<DataEntryDashboardPage />} />
            <Route path="/commissioning"     element={<PermissionGuard permKey="module.commissioning"><DataEntryModulePage moduleSlug="commissioning" /></PermissionGuard>} />
            <Route path="/estimating"        element={<PermissionGuard permKey="module.estimating"><DataEntryModulePage moduleSlug="estimating" /></PermissionGuard>} />
            <Route path="/purchasing"        element={<PermissionGuard permKey="module.purchasing"><DataEntryModulePage moduleSlug="purchasing" /></PermissionGuard>} />
            <Route path="/integration"       element={<PermissionGuard permKey="module.integration"><DataEntryModulePage moduleSlug="integration" /></PermissionGuard>} />
            <Route path="/submittals"        element={<PermissionGuard permKey="module.submittal"><DataEntryModulePage moduleSlug="submittals" /></PermissionGuard>} />
            <Route path="/sub-contractors"   element={<PermissionGuard permKey="module.subcontractor"><DataEntryModulePage moduleSlug="sub-contractors" /></PermissionGuard>} />
            <Route path="/qaqc"              element={<PermissionGuard permKey="module.qaqc"><DataEntryModulePage moduleSlug="qaqc" /></PermissionGuard>} />
            <Route path="/material-transfer" element={<PermissionGuard permKey="module.transmittal"><DataEntryModulePage moduleSlug="material-transfer" /></PermissionGuard>} />

            {/* Activity + Preferences */}
            <Route path="/activity-history" element={<ActivityHistoryPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />

            {/* Utilities */}
            <Route path="/utilities" element={<PermissionGuard permKey="feature.utilities.bulkimport"><UtilitiesDashboardPage /></PermissionGuard>} />
            <Route path="/import-template" element={<PermissionGuard permKey="feature.utilities.bulkimport"><ImportTemplatePage /></PermissionGuard>} />
            <Route path="/power-query" element={<PermissionGuard permKey="feature.utilities.powerquery"><PowerQueryPage /></PermissionGuard>} />

            {/* Integration section — gated by integration.integration (per-DB) */}
            <Route path="/integration-dashboard" element={<PermissionGuard permKey="integration.integration"><IntegrationDashboardPage /></PermissionGuard>} />
            <Route path="/io-buffer-generation" element={<PermissionGuard permKey="integration.integration"><CombinedToolsPage /></PermissionGuard>} />

            <Route path="/plc-platforms" element={<PermissionGuard permKey="integration.integration"><PlcPlatformsPage /></PermissionGuard>} />
            <Route path="/plc-tag-xref" element={<PermissionGuard permKey="integration.integration"><PlcTagXRefPage /></PermissionGuard>} />
            <Route path="/udt-comment-generator" element={<PermissionGuard permKey="integration.integration"><UdtCommentPage /></PermissionGuard>} />
            <Route path="/scada-tag-generation" element={<PermissionGuard permKey="integration.integration"><ScadaTagGenerationPage /></PermissionGuard>} />
            <Route path="/simulation-tag-generation" element={<PermissionGuard permKey="integration.integration"><SimulationTagGenerationPage /></PermissionGuard>} />

            {/* Reports */}
            <Route path="/reports" element={<PermissionGuard permKey="module.reports"><ReportsPage /></PermissionGuard>} />
            <Route path="/reports/:reportSlug" element={<PermissionGuard permKey="module.reports"><ReportViewerPage /></PermissionGuard>} />
            <Route path="/documentation-dashboard" element={<DocumentationDashboardPage />} />
            <Route path="/networking-dashboard" element={<NetworkingDashboardPage />} />
            <Route path="/equipment-dashboard" element={<EquipmentDashboardPage />} />

            {/* Catch-all */}
            <Route path="*" element={
              <UnderConstruction pageName={getPageDisplayName(location.pathname.split('/').slice(2).join('/'))} />
            } />
          </Routes>
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/**
 * AppRoutes — all routes, rendered inside Router + Auth + Lookup providers.
 */
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<DbRedirect />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/select-database" element={<DatabasePickerPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

      {/* Slug-free admin/dev pages — use AdminLayout with Outlet */}
      <Route element={<AdminLayout />}>
        {/* Admin: hardcoded SysAdmin — never permission-key driven so we can't lock ourselves out */}
        <Route path="/admin/dashboard" element={<SysAdminGuard><SecurityDashboardPage /></SysAdminGuard>} />
        <Route path="/admin/security" element={<SysAdminGuard><SecurityOverviewPage /></SysAdminGuard>} />
        <Route path="/admin/users" element={<SysAdminGuard><AdminUsersPage /></SysAdminGuard>} />
        <Route path="/admin/roles" element={<SysAdminGuard><RolesCapabilitiesPage /></SysAdminGuard>} />
        <Route path="/admin/permissions" element={<SysAdminGuard><PermissionsPage /></SysAdminGuard>} />
        <Route path="/admin/databases" element={<SysAdminGuard><DatabasesPage /></SysAdminGuard>} />
        <Route path="/admin/organizations" element={<SysAdminGuard><OrganizationsPage /></SysAdminGuard>} />
        <Route path="/admin/system-health" element={<SysAdminGuard><SystemHealthPage /></SysAdminGuard>} />
        <Route path="/admin/app-users" element={<SysAdminGuard><UnderConstruction pageName="Users" /></SysAdminGuard>} />
        {/* Dev tools: gated by development.development permission (global — not DB-scoped) */}
        <Route path="/development-dashboard" element={<GlobalPermissionGuard permKey="development.development"><DevelopmentDashboardPage /></GlobalPermissionGuard>} />
        <Route path="/sample-table-view" element={<GlobalPermissionGuard permKey="development.development"><SampleTableViewPage /></GlobalPermissionGuard>} />
        <Route path="/task-log" element={<GlobalPermissionGuard permKey="development.development"><TaskLogPage /></GlobalPermissionGuard>} />
        <Route path="/dev-plan" element={<GlobalPermissionGuard permKey="development.development"><DevPlanPage /></GlobalPermissionGuard>} />
        <Route path="/schema-viewer" element={<GlobalPermissionGuard permKey="development.development"><SchemaViewerPage /></GlobalPermissionGuard>} />
        <Route path="/field-manager" element={<GlobalPermissionGuard permKey="development.development"><FieldManagerPage /></GlobalPermissionGuard>} />
        <Route path="/claude-notes" element={<GlobalPermissionGuard permKey="development.development"><ClaudeNotesPage /></GlobalPermissionGuard>} />
        <Route path="/icon-browser" element={<GlobalPermissionGuard permKey="development.development"><IconBrowserPage /></GlobalPermissionGuard>} />
        <Route path="/rev-log" element={<GlobalPermissionGuard permKey="development.development"><RevLogPage /></GlobalPermissionGuard>} />
        <Route path="/standard-view-config" element={<GlobalPermissionGuard permKey="development.development"><ListViewConfigPage /></GlobalPermissionGuard>} />
        <Route path="/lookup-values" element={<GlobalPermissionGuard permKey="development.development"><LookupValuesPage /></GlobalPermissionGuard>} />
        <Route path="/display-settings" element={<GlobalPermissionGuard permKey="development.development"><DisplaySettingsPage /></GlobalPermissionGuard>} />
        <Route path="/database-sync" element={<GlobalPermissionGuard permKey="development.development"><SyncDashboardPage /></GlobalPermissionGuard>} />
        <Route path="/view-synchronizer" element={<GlobalPermissionGuard permKey="development.development"><ViewSyncPage /></GlobalPermissionGuard>} />
        <Route path="/schema-synchronizer" element={<GlobalPermissionGuard permKey="development.development"><SchemaSyncPage /></GlobalPermissionGuard>} />
        <Route path="/style-guide" element={<GlobalPermissionGuard permKey="development.development"><StyleGuidePage /></GlobalPermissionGuard>} />
        <Route path="/data-entry-view-config" element={<GlobalPermissionGuard permKey="development.development"><DataEntryComposerPage /></GlobalPermissionGuard>} />
      </Route>

      {/* Data pages — need DB slug */}
      <Route path="/:dbSlug/*" element={
        <DatabaseProvider>
          <AppContent />
        </DatabaseProvider>
      } />
    </Routes>
  );
};


// Apply stored UI zoom level on startup
const storedZoom = localStorage.getItem('dawn.uiZoom');
if (storedZoom && storedZoom !== '100%') {
  document.documentElement.style.zoom = storedZoom;
}

// Root App
const App = () => {
  if (isEntraAuth()) {
    return (
      <MsalProviderWrapper>
        <NotificationProvider>
          <EntraAuthProvider>
            <LookupProvider>
              <ActivityTrackerProvider>
                <Router>
                  <AuthGate>
                    <AppRoutes />
                  </AuthGate>
                </Router>
              </ActivityTrackerProvider>
            </LookupProvider>
          </EntraAuthProvider>
        </NotificationProvider>
      </MsalProviderWrapper>
    );
  }

  return (
    <NotificationProvider>
      <ImpersonationAuthProvider>
        <LookupProvider>
          <ActivityTrackerProvider>
            <Router>
              <AuthGate>
                <AppRoutes />
              </AuthGate>
            </Router>
          </ActivityTrackerProvider>
        </LookupProvider>
      </ImpersonationAuthProvider>
    </NotificationProvider>
  );
};

export default App;
